<template>
    <div>
        <!-- ─ App Bar ─────────────────────────────────────────────── -->
        <v-app-bar app :class="classMenu" :style="cssVars">
            <v-img
                @click="scrollTo('top')"
                max-width="80"
                contain
                eager
                :src="getLogo"
                style="cursor: pointer;"
            />
            <v-spacer />

            <v-chip
                color="white"
                text-color="primary"
                class="font-weight-bold px-4"
                style="cursor: pointer; user-select: none; font-size: 15px;"
                @mousedown="startLongPress"
                @mouseup="cancelLongPress"
                @mouseleave="cancelLongPress"
                @touchstart.prevent="startLongPress"
                @touchend="cancelLongPress"
                @touchcancel="cancelLongPress"
            >
                {{ $t('tablet.mesa', { n: getMesa }) }}
            </v-chip>

            <v-spacer />

            <SeletorIdioma class="mr-1" />

            <!-- ─ Botão Minha Conta ─────────────────────────────── -->
            <button class="nav-action-btn" @click="minhaConta">
                <div class="nav-action-btn__icon">
                    <v-icon size="22" color="white">mdi-receipt-text-edit-outline</v-icon>
                </div>
                <div class="nav-action-btn__text">
                    <span class="nav-action-btn__label">{{ $t('tablet.minhaConta1') }}</span>
                    <span class="nav-action-btn__label">{{ $t('tablet.minhaConta2') }}</span>
                </div>
            </button>

            <!-- ─ Botão Carrinho ──────────────────────────────────── -->
            <button class="nav-action-btn nav-action-btn--cart" @click="carrinho">
                <div class="nav-action-btn__icon">
                    <v-icon size="22" color="white">mdi-cart-outline</v-icon>
                    <span v-if="itensNoCarrinho > 0" class="nav-action-btn__contador">
                        {{ itensNoCarrinho > 99 ? '99+' : itensNoCarrinho }}
                    </span>
                </div>
                <span class="nav-action-btn__single-label">{{ $t('tablet.carrinho') }}</span>
            </button>

            <v-btn
                v-if="!search"
                @click="openSearch"
                icon
                :color="$vuetify.theme.contrast.color"
            >
                <v-icon>mdi-magnify</v-icon>
            </v-btn>

            <v-text-field
                v-if="search"
                ref="search"
                v-model="searchTerm"
                @input="$emit('typing', searchTerm)"
                :label="$t('cardapio.pesquisarProduto')"
                prepend-inner-icon="mdi-magnify"
                style="max-width: 180px;"
                hide-details
                outlined
                dense
                dark
            />

            <v-btn
                v-if="search"
                @click="closeSearch"
                icon
                :color="$vuetify.theme.contrast.color"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-app-bar>

        <!-- ─ Sidebar permanente de categorias ───────────────────── -->
        <v-navigation-drawer
            permanent
            app
            touchless
            :mobile-breakpoint="0"
            :value="true"
            :width="200"
            :height="drawerHeight"
            :style="classSideMenu"
        >
            <!-- ─ Logo do cliente ────────────────────────────── -->
            <div class="sidebar-logo-area">
                <img
                    v-if="getClienteLogo"
                    :src="getClienteLogo"
                    class="sidebar-logo"
                    alt="Logo"
                />
            </div>

            <!-- ─ Categorias ───────────────────────────────── -->
            <div ref="categorias" class="sidebar-categories">
                <div
                    v-for="categoria in categorias"
                    :key="categoria.descricao"
                    class="sidebar-item"
                    :class="{ 'sidebar-item--active': activeCategory === categoria.descricao }"
                    @click="onCategoryClick(categoria.descricao)"
                >
                    <!-- Ícone SVG dinâmico -->
                    <span
                        v-if="categoria.icone && getSvgUrl(categoria.icone)"
                        class="svg-icon"
                        :style="{
                            'background-color': activeCategory === categoria.descricao ? $theme.primary : (isDark ? '#bbb' : '#888'),
                            '-webkit-mask-image': `url(${getSvgUrl(categoria.icone)})`,
                            'mask-image': `url(${getSvgUrl(categoria.icone)})`,
                        }"
                    />
                    <!-- Fallback: ícone padrão quando grupo não tem ícone -->
                    <v-icon
                        v-else
                        size="30"
                        :color="activeCategory === categoria.descricao ? $theme.primary : (isDark ? '#bbb' : '#888')"
                    >
                        mdi-silverware-fork-knife
                    </v-icon>

                    <div
                        class="sidebar-item__label"
                        :style="`color: ${activeCategory === categoria.descricao ? $theme.primary : (isDark ? '#ddd' : '#555')};`"
                    >
                        {{ categoria.descricao }}
                    </div>
                </div>
            </div>
        </v-navigation-drawer>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { getIconUrl } from '@/utils/categoriaIcons';
import SeletorIdioma from '@/components/SeletorIdioma';

export default {
    name: 'Menu',

    components: { SeletorIdioma },

    props: {
        categorias: {
            type: Array,
            default: () => [],
        },
        itensNoCarrinho: {
            type: Number,
            default: 0,
        },
    },

    data: () => ({
        menu: { text: '', back: '' },
        search: false,
        searchTerm: '',
        longPressTimer: null,
        activeCategory: '',
    }),

    computed: {
        ...mapState([
            'configuracoes',
            'tablet',
        ]),

        classMenu() {
            return this.$vuetify.theme.isDark ? 'app-bar-dark' : 'app-bar-light';
        },

        classSideMenu() {
            return this.$vuetify.theme.isDark
                ? 'background-color: #111 !important; border-right: 1px solid #2a2a2a;'
                : 'background-color: #f5f5f5 !important; border-right: 1px solid #e0e0e0;';
        },

        drawerHeight() {
            // `vh` colapsa para 0 no WebView (Chromium 111); `%` funciona. Ver [[project_zaper_tablet]].
            return '100%';
        },

        cssVars() {
            return {
                '--bg-color': this.$vuetify.theme.custom.appBarColor,
                'padding': '0 0 10px 0',
            };
        },

        getLogo() {
            const theme = this.$vuetify.theme.contrast.theme || 'dark';
            const logo = require(`@/assets/zapermenu-${theme}.png`);
            return logo;
        },

        getMesa() {
            return this.tablet?.mesa || '';
        },

        isDark() {
            return this.$vuetify.theme.isDark;
        },

        getClienteLogo() {
            const logo = this.configuracoes?.logo;
            if (!logo?.url) return null;
            return logo.tmp
                ? `${this.$urlAPI}/files/tmp/${logo.url}`
                : `${this.$urlAPI}/storage/${logo.url}`;
        },
    },

    created() {
        // Enquanto o cardápio faz a rolagem suave até a categoria clicada, o
        // scroll-spy atravessa todos os grupos do caminho. Sem esta janela, o
        // destaque piscava por dez categorias e a lista lateral ia junto.
        this.ignorarSpyAte = 0;

        this.$root.$on('menu', menu => (this.menu = menu));
        // Ouve o scroll-spy do Cardapio.vue
        this.$root.$on('tablet:activeCategory', (cat) => {
            if (Date.now() < this.ignorarSpyAte) return;
            this.activeCategory = cat;
        });
        // "Limpar busca" do estado vazio (Cardapio.vue) fecha o campo daqui.
        this.$root.$on('tablet:clearSearch', () => this.closeSearch());
    },

    watch: {
        categorias: {
            immediate: true,
            handler(val) {
                if (val && val.length && !this.activeCategory) {
                    this.activeCategory = val[0].descricao;
                }
            },
        },

        activeCategory() {
            this.$nextTick(this.manterCategoriaVisivel);
        },
    },

    methods: {
        startLongPress() {
            this.longPressTimer = setTimeout(() => {
                this.$root.$emit('openTabletConfig');
            }, 3000);
        },

        cancelLongPress() {
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer);
                this.longPressTimer = null;
            }
        },

        minhaConta() {
            this.$root.$emit('openCheck');
        },

        carrinho() {
            this.$root.$emit('openCart');
        },

        onCategoryClick(descricao) {
            this.activeCategory = descricao;
            // Cobre a rolagem suave do cardápio até o grupo escolhido.
            this.ignorarSpyAte = Date.now() + 1000;
            this.scrollTo(descricao);
        },

        /**
         * Rola a lista lateral o mínimo necessário para a categoria ativa aparecer.
         */
        manterCategoriaVisivel() {
            const lista = this.$refs.categorias;
            const ativo = lista && lista.querySelector('.sidebar-item--active');

            if (!lista || !ativo) return;

            const folga = 12;
            const topoItem = ativo.offsetTop;
            const fimItem = topoItem + ativo.offsetHeight;
            const topoVisivel = lista.scrollTop;
            const fimVisivel = topoVisivel + lista.clientHeight;

            if (topoItem < topoVisivel + folga) {
                lista.scrollTo({ top: topoItem - folga, behavior: 'smooth' });
            } else if (fimItem > fimVisivel - folga) {
                lista.scrollTo({ top: fimItem - lista.clientHeight + folga, behavior: 'smooth' });
            }
        },

        scrollTo(categoria) {
            if (categoria === 'top') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            const el = document.querySelector('#' + categoria.replace(/\s/g, '-'));
            if (!el) return;
            const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
            window.scrollTo({ top: y, behavior: 'smooth' });
        },

        getSvgUrl(name) {
            return getIconUrl(name);
        },

        openSearch() {
            this.search = true;
            setTimeout(() => this.$refs.search.focus(), 100);
        },

        closeSearch() {
            this.search = false;
            this.searchTerm = '';
            this.$emit('typing', '');
        },
    },
};
</script>

<style scoped>
.v-navigation-drawer--fixed {
    transform: none !important;
    visibility: visible !important;
    touch-action: pan-y;
}

.app-bar-dark {
    background-color: var(--bg-color) !important;
}

.app-bar-light {
    background-color: var(--bg-color) !important;
}

/* ── Logo do cliente ──────────────────────────────────────── */

.sidebar-logo-area {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px 16px 14px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.15);
}

.sidebar-logo {
    width: 130px;
    height: 130px;
    object-fit: cover;
    border-radius: 18px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.sidebar-logo:hover {
    transform: scale(1.03);
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.18);
}

.sidebar-categories {
    padding: 8px 0;
    overflow-y: auto;
    height: calc(100% - 182px);
    position: relative;
    overscroll-behavior: contain;
}

.sidebar-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 76px;
    padding: 12px 8px;
    margin: 4px 10px;
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
    text-align: center;
    border: 2px solid transparent;
    user-select: none;
}

.sidebar-item:hover {
    background: rgba(128, 128, 128, 0.12);
}

.sidebar-item--active {
    border-color: var(--v-primary-base);
    background: rgba(128, 128, 128, 0.1);
}

.sidebar-item__label {
    font-size: 12px;
    font-weight: 600;
    margin-top: 6px;
    line-height: 1.25;
    letter-spacing: 0.3px;
    max-width: 160px;
    word-break: break-word;
}

/* ── Ícone SVG dinâmico (mask para controle de cor) ──────── */

.svg-icon {
    display: inline-block;
    width: 30px;
    height: 30px;
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    transition: background-color 0.2s ease;
}
/* ── Botões de ação da app bar ───────────────────────────── */

.nav-action-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 18px 8px 8px;
    margin: 0 5px;
    border-radius: 50px;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1.5px solid rgba(255, 255, 255, 0.28);
    color: white;
    cursor: pointer;
    transition: background 0.22s ease, border-color 0.22s ease, transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.22s ease;
    outline: none;
    white-space: nowrap;
    min-height: 50px;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
}

.nav-action-btn:hover {
    background: rgba(255, 255, 255, 0.26);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
}

.nav-action-btn:active {
    transform: translateY(0) scale(0.97);
    box-shadow: none;
}

.nav-action-btn__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    flex-shrink: 0;
    transition: background 0.2s ease;
}

.nav-action-btn:hover .nav-action-btn__icon {
    background: rgba(255, 255, 255, 0.3);
}

.nav-action-btn__text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.1;
}

.nav-action-btn__label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    letter-spacing: 0.4px;
}

.nav-action-btn__single-label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    letter-spacing: 0.4px;
}

.nav-action-btn__icon { position: relative; overflow: visible; }

.nav-action-btn__contador {
    position: absolute;
    top: -5px;
    right: -6px;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: #fff;
    color: var(--v-primary-base);
    font-size: 12px;
    font-weight: 800;
    line-height: 22px;
    text-align: center;
    box-shadow: 0 0 0 2px var(--bg-color, #000), 0 2px 6px rgba(0, 0, 0, 0.3);
}

/* Botão Carrinho com destaque leve de cor */
.nav-action-btn--cart {
    background: rgba(255, 255, 255, 0.2);
}

.nav-action-btn--cart:hover {
    background: rgba(255, 255, 255, 0.3);
}
</style>
